import { Box, Group, useMantineTheme } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import { SettingsButton } from '@repo/common/components';
import { Icon, Text } from '@repo/foundations';
import { documentImporter } from '@repo/secoda-editor/importer';
import { useCallback } from 'react';
import { createDocument, useSuspenseFeatureAccess } from '../../../api';
import { openModal } from '../../ModalManager';
import { UpgradeButton } from '../UpgradeButton';

export function DocumentImport() {
	const { exportAccess } = useSuspenseFeatureAccess();
	const theme = useMantineTheme();

	const handleConfluence = async (files: File[]) => {
		const created = [];
		for (const file of files) {
			const map = {
				fileName: file.name,
				mimeType: file.type,
			};
			const { title, text } = await documentImporter(file, map);
			createDocument({ data: { title, definition: text } });
			created.push(title);
		}

		showNotification({
			title: 'Documents imported',
			message: `Imported ${created.length} documents: ${created.join(', ')}`,
			icon: <Icon name="check" color="text/success-on-fill/default" />,
			// eslint-disable-next-line no-theme-colors/no-theme-colors
			color: theme.colors.green[theme.colorScheme === 'dark' ? 4 : 6],
		});
	};

	const openDocumentsModal = useCallback(() => {
		if (!exportAccess) {
			return;
		}

		openModal({
			title: 'Import Documents',
			centered: true,
			children: (
				<Box>
					<Dropzone onDrop={handleConfluence} maxSize={16 * 1024 ** 2}>
						<Group
							position="center"
							spacing="md"
							style={{
								marginTop: 20,
								marginBottom: 20,
								minHeight: 140,
								pointerEvents: 'none',
							}}
						>
							<Dropzone.Accept>
								<Icon name="upload" size="lg" />
							</Dropzone.Accept>
							<Dropzone.Reject>
								<Icon name="x" size="lg" color="icon/critical/default" />
							</Dropzone.Reject>
							<Dropzone.Idle>
								<Icon name="file" color="icon/emphasis/default" size="lg" />
							</Dropzone.Idle>
							<Box>
								<Text
									align="center"
									color="text/secondary/default"
									inline
									mt={7}
								>
									Drag and drop .html or .doc files from Confluence's export
									options, or click to upload
								</Text>
							</Box>
						</Group>
					</Dropzone>
					<Text mt={20} mb={10} size="xs" color="text/secondary/default">
						{
							'For html upload, navigate to Space Settings > Manage space > Export space and choose to export as HTML with the "Normal Export" option. You will need to unzip the file and upload each html file separately. Maximum file size is 16MB.'
						}
					</Text>
				</Box>
			),
		});
	}, [exportAccess, handleConfluence]);

	return (
		<SettingsButton
			title="Documents"
			description="Import documents from Confluence, Google Docs, and Microsoft Word"
			buttonText="Import documents"
			onClick={openDocumentsModal}
		>
			{!exportAccess && <UpgradeButton feature="Documents Import" />}
		</SettingsButton>
	);
}
